import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
// Blueprint Tabs replaced with Skyline custom tabs
import { useParams, useNavigate } from 'react-router-dom';
import RecordDetailForm from './RecordDetailForm';
import RecordRelatedList from './RecordRelatedList';
import GmailRecordEmailTab from './GmailRecordEmailTab';
import RecordHistory from './RecordHistory';
import { DetailActionBar } from './DetailActions/DetailActionBar';
import type { DetailAction } from './DetailActions/types';
import { useGlobalMetadata } from '../utils/providers';
import { useDeletedRecord } from './RecordDetailForm/hooks/useDeletedRecord';
import { workflowBreadcrumb } from '../utils/telemetry';

// Type imports
import type {
  UIComponentMetadata,
  RouteMetadata,
  TableInfo
} from '../types/metadata';
import type { LegacyTabConfig } from '../types/record-form';

// Component props interface
interface RecordDetailPageProps {
  recordId?: number | string;
  displayRecordId?: number | string;
  config?: UIComponentMetadata;
  onDeleted?: () => void;
  manualSave?: boolean;
}

// URL params interface - use string index signature for React Router params
interface RouteParams extends Record<string, string | undefined> {
  table?: string;
  id?: string;
}

// Tab component props interface
interface TabData {
  name: string;
  component_type: 'Form' | 'List' | 'Custom';
  table_name: string;
  schema_name?: string;
  custom_component?: string;
  [key: string]: any;
}

const RecordDetailPage = memo<RecordDetailPageProps>(({ recordId, displayRecordId, config, onDeleted, manualSave = false }) => {
  const { data: metadata } = useGlobalMetadata();
  const { table: tableFromUrl, id: idFromUrl } = useParams<RouteParams>();

  // Type-safe access to route data
  const routeData = metadata?.routeData as RouteMetadata | null;
  const configFromUrl = routeData?.primaryUiComponentByRouteName?.[tableFromUrl as string]?.detail || null;

  const effectiveRecordId = recordId || idFromUrl;
  const effectiveConfig = config || configFromUrl;

  // Get table metadata for deletion check
  const tableName = effectiveConfig?.table_name || '';
  const schemaName = effectiveConfig?.schema_name || metadata?.tableData?.byName?.[tableName]?.schema_name || 'public';

  // Check if viewing a deleted record to hide related tabs
  const { wasDeleted } = useDeletedRecord({
    tableName,
    schemaName,
    recordId: Number(effectiveRecordId),
    isEnabled: !!effectiveRecordId && !!tableName && metadata?.tableData?.byName?.[tableName]?.audit_logs === true
  });
  
  

  // Use the first tab as the default tab.
  const defaultTab = encodeURIComponent(effectiveConfig?.ui_data?.tabs?.[0]?.name || 'tab-0');

  // Store active tab in local state only, not in the URL
  const [activeTabId, setActiveTabId] = useState<string>(defaultTab);
  // Do not mount data-heavy hidden tabs when a record opens. Once visited, a
  // tab remains mounted so its grid/form state is preserved while switching.
  const [visitedTabIds, setVisitedTabIds] = useState<Set<string>>(() => new Set([defaultTab]));

  if (!effectiveConfig) {
    throw new Error('No config found for this route');
  }

  useEffect(() => {
    setActiveTabId(defaultTab);
    setVisitedTabIds(new Set([defaultTab]));
  }, [defaultTab, effectiveRecordId]);

  useEffect(() => {
    if (!tableName || !effectiveRecordId) return;
    workflowBreadcrumb('record.detail.opened', {
      table: tableName,
      recordId: String(effectiveRecordId),
      surface: recordId ? 'embedded' : 'route',
    });
  }, [effectiveRecordId, recordId, tableName]);

  // Prepare tab data (id, title, panel content)
  interface TabItem { id: string; title: string; panel: React.ReactNode }
  const tabs: TabItem[] = useMemo(() => {
    const result: TabItem[] = [];
    const tabsArray = effectiveConfig?.ui_data?.tabs as TabData[] | undefined;

    tabsArray?.forEach((tabConfig: TabData) => {
      const { name, component_type } = tabConfig;
      const tabId = encodeURIComponent(name);

      const tableInfoForTab = metadata?.tableData?.byName?.[tabConfig.table_name];
      const enrichedTabConfig: LegacyTabConfig = {
        table_name: tabConfig.table_name,
        schema_name: tabConfig.schema_name || tableInfoForTab?.schema_name || 'public',
        sections: tabConfig.sections || [],
        editable: tabConfig.editable,
        showDelete: tabConfig.showDelete,
      };

      let panelContent: React.ReactNode;
      if (component_type === 'Form') {
        panelContent = (
          <RecordDetailForm
            tabConfig={enrichedTabConfig}
            recordId={Number(effectiveRecordId)}
            onDeleted={onDeleted}
            manualSave={manualSave}
          />
        );
      } else if (component_type === 'List') {
        const tableToRouteName = routeData?.tableToRouteName;
        const routeNameForTab = tableToRouteName?.[tabConfig.table_name];
        panelContent = (
          <RecordRelatedList
            routeName={routeNameForTab || tabConfig.table_name}
            tabConfig={tabConfig as TabData & { parent_field: string }}
            recordId={Number(effectiveRecordId)}
            parentRecordDeleted={wasDeleted === true}
          />
        );
      } else if (component_type === 'Custom') {
        const customTableName = tabConfig.table_name || effectiveConfig.table_name;
        if (tabConfig.custom_component === 'gmail_record_email_tab') {
          panelContent = (
            <GmailRecordEmailTab
              tableName={customTableName}
              recordId={Number(effectiveRecordId)}
            />
          );
        } else {
          panelContent = <div>Unknown custom component: {tabConfig.custom_component}</div>;
        }
      } else {
        panelContent = <div>Unknown component type: {component_type}</div>;
      }

      result.push({ id: tabId, title: name, panel: panelContent });
    });

    if (effectiveConfig?.ui_data?.showRecordHistory) {
      result.push({
        id: 'history',
        title: 'History',
        panel: (
          <RecordHistory
            tableName={effectiveConfig.table_name}
            recordId={Number(effectiveRecordId)}
          />
        ),
      });
    }

    return result;
  }, [effectiveConfig, effectiveRecordId, metadata, routeData, wasDeleted]);

  const handleTabChange = useCallback(
    (newTabId: string) => {
      setActiveTabId(newTabId);
      setVisitedTabIds((current) => {
        if (current.has(newTabId)) return current;
        return new Set(current).add(newTabId);
      });
    },
    []
  );

  const actions = effectiveConfig?.ui_data?.actions as DetailAction[] | undefined;
  const actionRecord = useMemo(
    () => ({ id: effectiveRecordId } as Record<string, unknown>),
    [effectiveRecordId],
  );

  // Get table display label for the header
  const tableInfo = metadata?.tableData?.byName?.[tableName] as TableInfo | undefined;
  const tableLabel = tableInfo?.ui_label || tableName;

  const navigate = useNavigate();
  const routeData2 = metadata?.routeData as RouteMetadata | null;
  const listRouteName = routeData2?.tableToRouteName?.[tableName];

  return (
    <div data-testid="record-detail">
      {/* Page header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '4px 0 12px',
        marginBottom: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {listRouteName && (
            <svg
              width={16} height={16} viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ cursor: 'pointer', opacity: 0.6, flexShrink: 0 }}
              onClick={() => navigate(`/${listRouteName}`)}
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          )}
          <span style={{
            fontSize: 15,
            fontWeight: 600,
            opacity: 0.75,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {tableLabel}
          </span>
          <span style={{ fontSize: 13, opacity: 0.45, flexShrink: 0 }}>
            #{displayRecordId ?? effectiveRecordId}
          </span>
        </div>
        {actions && actions.length > 0 && (
          <DetailActionBar actions={actions} record={actionRecord} tableName={tableName} />
        )}
      </div>

      {/* Skyline custom tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border-soft)', marginBottom: 16 }}>
        {tabs.map((tab) => (
          <div
            key={tab.id}
            data-testid="detail-tab"
            onClick={() => handleTabChange(tab.id)}
            style={{
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: activeTabId === tab.id ? 600 : 450,
              color: activeTabId === tab.id ? 'var(--text-1)' : 'var(--text-3)',
              cursor: 'pointer',
              borderBottom: activeTabId === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1,
              transition: 'all 0.12s',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
          >
            {tab.title}
          </div>
        ))}
      </div>

      {/* Mount each tab only on first visit; keep visited tabs mounted for state preservation. */}
      {tabs.map((tab) => (
        visitedTabIds.has(tab.id) && (
          <div
            key={tab.id}
            style={{ display: activeTabId === tab.id ? 'block' : 'none' }}
          >
            {tab.panel}
          </div>
        )
      ))}
    </div>
  );
});

RecordDetailPage.displayName = 'RecordDetailPage';

export default RecordDetailPage;
